import { useEffect, useState, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS
import * as StyledComponents from '../StyledComponents';
import { GridApi, ColumnApi, ExcelStyle } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { AG_GRID_LICENSE } from '../../../../constants';
import OutageNotificationListHeader from './OutageNotificationListHeader';
import { AdminTableBodySkeleton } from '../../Blocks/AdminTableBodySkeleton';
import moment from 'moment';
import { useUnifyAdminStore } from '../../AdminStore';
import {
  strToDate,
  formattedTime,
  getLocalizationValues
} from '../../utils';
import { DeleteConfirmDialog } from '../Elements/DeleteConfirmDialog';
import {useAllApplicationsStore, useAllWorkflowsStore} from '../../../../Store/index'
import {useGetAllNotifications,useRemoveQuery} from '../Store/useOutageNotificationQuery';
import { Overlay } from './Overlay';
import {excelConfig, defaultColDef, getColumnDefs} from './OutageGridConfig';
import { useUserProfile } from '../../../../hooks/queries';
import { useTranslation } from 'react-i18next';

LicenseManager.setLicenseKey(AG_GRID_LICENSE);

export default function OutageNotificationList(): JSX.Element {
  const { t } = useTranslation();
  const {data: userProfile} = useUserProfile();
  
  const applications = useAllApplicationsStore((state:any) => state.applications);
  const workflows = useAllWorkflowsStore((state:any) => state.workflows);

  const sideSheetData = useUnifyAdminStore(
    (state) => state.productOutageSideSheetData
  );
  const archiveSwitch = useUnifyAdminStore((state) => state.productOutageArchiveSwitch);

  const setProductOutageShowToastState = useUnifyAdminStore(
    (state) => state.setProductOutageShowToastState
  );

  const setSideSheetData = useUnifyAdminStore(
    (state) => state.setProductOutageSideSheetData
  );
  
  const setProductOutageSideSheetCollapse = useUnifyAdminStore(
    (state) => state.setProductOutageSideSheetCollapse
  );

  const setOutageNotifications = useUnifyAdminStore(
    (state) => state.setOutageNotifications
  );

  const deleteOutage = () =>{
    deleteMutation.mutate({messageId:sideSheetData.messageId});
  }

  const deleteDialogOpen = useUnifyAdminStore(
    (state) => state.deleteDialogOpen
  );
  const setDeleteDialogOpen = useUnifyAdminStore(
    (state) => state.setDeleteDialogOpen
  );
  const searchValue = useUnifyAdminStore(
    (state) => state.productOutageSearchValue
  );

  const productOutageSideSheetData = useUnifyAdminStore(
    (state) => state.productOutageSideSheetData
  );
  const outagePermission = useUnifyAdminStore(
    (state) => state.permissions
  );

  const setShowStartDateValidation = useUnifyAdminStore((state) => state.setShowStartDateValidation);
  const setShowEndDateValidation = useUnifyAdminStore((state) => state.setShowEndDateValidation);

  const {mutation:deleteMutation} = useRemoveQuery({
    onSuccess: () => {
      setProductOutageShowToastState(t('productAlerts.toastDeleteSuccess', { title: getLocalizationValues(userProfile, sideSheetData).title }), 'Success');
    },
    onError: () => {
      setProductOutageShowToastState(t('productAlerts.toastDeleteError'), 'Error');
    },
  });


  const [gridApi, setGridApi] = useState<GridApi>();
  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    if (gridApi) {
      gridApi.setQuickFilter(searchValue);
    }
  }, [searchValue, gridApi]);

  const { data: listOfOutageNotification, isFetching } =
  useGetAllNotifications(archiveSwitch);

  useEffect(()=>{
    if(listOfOutageNotification){
      setOutageNotifications(listOfOutageNotification);
    }
  },[listOfOutageNotification,setOutageNotifications])

  const onGridReady = (params: { api: GridApi; columnApi: ColumnApi }) => {
    setGridApi(params.api);
  };

  const cellClickedListener = useCallback((event: any) => {
    let data = { ...event.data };

    setShowStartDateValidation(false);
    setShowEndDateValidation(false);

    if(event.colDef.colId === 'Duplicate'){
      data.title = "Copy of ".concat(data.title);
      data.messageId = null;
      data.caseId = "";
    }

    if (event.data.caseId) {
      data.outageType = 'Unplanned';
    } else {
      data.outageType = 'Planned';
    }

    if (data.createdDate) {
      const dateTime = strToDate(data.createdDate);
      data.createdTime = formattedTime(dateTime);
      data.createdDate = moment(dateTime);
    }

    if (data.startDate) {
      const dateTime = strToDate(data.startDate);
      data.startTime = formattedTime(dateTime);
      data.startDate = moment(dateTime);
    }

    if (data.endDate) {
      const dateTime = strToDate(data.endDate);
      data.endTime = formattedTime(dateTime);
      data.endDate = moment(dateTime);
    }

    if (event.data.removed) {
      data.status = 'Closed';
    } else {
      data.status = 'Open';
    }


    data.targetApplications = applications && applications[0]
      .filter((app: any) => event.data.target?.application?.includes(app.appId))
      .map((app: any) => ({ id: app.appId, label: app.name, value: app.appId }));

    if (workflows && workflows[0]) {
      data.targetWorkflows = workflows[0]
        .filter((workflow: any) =>
          event.data.target.workflow?.includes(workflow.workflowId)
        )
        .map((workflow: any) => ({
          id: workflow.workflowId,
          label: workflow.name,
          value: workflow.workflowId,
        }));
    }

    data.targetEnterprises = event.data.target.enterpriseIds?.join(', ')||'';
    data.uploadedEnterprises = event.data.target.enterpriseIds?.join(', ')||'';
    data.targetStores = event.data.target.stores?.join(', ')||'';
    data.uploadedStores = event.data.target.stores?.join(', ')||'';
    data.targetCNumbers = event.data.target.cnumbers?.join(', ')||'';
    data.uploadedCNumbers = event.data.target.cnumbers?.join(', ')||'';
    data.linkToLearnMore = data.externalUrl;
    data.linkToLearnMoreLocale = data.externalUrlLocale;

    setSideSheetData(data);

    if (event.colDef.colId !== 'Delete') {
      setProductOutageSideSheetCollapse(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let columns = getColumnDefs(outagePermission, applications, workflows, archiveSwitch, t);

  return (
    <>
      <OutageNotificationListHeader gridRef={gridRef} />
      {deleteDialogOpen && (
        <DeleteConfirmDialog
        title={getLocalizationValues(userProfile, productOutageSideSheetData).title}
        subject={getLocalizationValues(userProfile, productOutageSideSheetData).subject}
          isOpen={true}
          onYes={() => {
            deleteOutage();
            setDeleteDialogOpen(false);
          }}
          onNo={() => {
            setDeleteDialogOpen(false);
          }}
        ></DeleteConfirmDialog>
      )}
      <Overlay isOpen={deleteDialogOpen} panelId="panel-3"/>
      {isFetching ? (
        <AdminTableBodySkeleton />
      ) : (
        <StyledComponents.StyledTableContainer>
          <StyledComponents.StyledTableWrapper className="ag-theme-alpine">
            <AgGridReact
              ref={gridRef}
              rowData={listOfOutageNotification}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              animateRows={true}
              rowSelection="single"
              excludeHiddenColumnsFromQuickFilter={true}
              suppressDragLeaveHidesColumns={true}
              defaultExcelExportParams={excelConfig}
              excelStyles={excelConfig.excelStyles as ExcelStyle[]}
              onCellClicked={cellClickedListener}
            />
          </StyledComponents.StyledTableWrapper>
        </StyledComponents.StyledTableContainer>
      )}
    </>
  );
}
